'use client';

import React, { useEffect, useRef } from 'react';

const CHECKLIST_ITEMS = [
  {
    title: 'Debt Consolidation',
    desc: 'Combine multiple high-interest debts into one manageable monthly payment with a lower rate.',
  },
  {
    title: 'Home Improvement',
    desc: 'Fund renovations, repairs, or upgrades without tapping into home equity.',
  },
  {
    title: 'Major Purchases',
    desc: 'Finance large one-time expenses — appliances, vehicles, medical procedures — on your terms.',
  },
  {
    title: 'Emergency Fund',
    desc: 'Cover unexpected costs quickly with same-day or next-day funding options available.',
  },
];

const RATES = [
  { label: 'Loan Amounts', value: '$1,000 – $100,000' },
  { label: 'APR Range', value: '6.99% – 35.99%' },
  { label: 'Loan Terms', value: '12 – 84 Months' },
  { label: 'Funding Speed', value: 'As fast as 24 hrs' },
  { label: 'Min. Credit Score', value: '580+' },
];

export default function PersonalLoans() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    sectionRef?.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="personal-loans" className="py-24 lg:py-32 bg-cloud overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section label */}
        <div className="mb-14 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full bg-sky-light border border-sky/20 text-xs font-semibold text-sky-dark font-body uppercase tracking-wide mb-5">
            Personal Financing
          </span>
          <h2 className="font-heading font-bold text-display text-slate">
            Personal Loans
          </h2>
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* LEFT: Checklist */}
          <div className="reveal-left">
            <p className="font-body text-slate-fog text-base leading-relaxed mb-10">
              Whether you're simplifying debt or funding a life milestone, our personal loan network connects you with lenders who match your profile — not just your credit score.
            </p>

            <div className="space-y-5">
              {CHECKLIST_ITEMS?.map((item, idx) => (
                <div
                  key={item?.title}
                  className="flex gap-4 p-5 rounded-3xl bg-white border border-slate-mist/40 hover:border-sky/30 hover:shadow-card transition-all duration-300"
                  style={{ transitionDelay: `${idx * 80}ms` }}
                >
                  {/* Check icon */}
                  <div className="shrink-0 w-8 h-8 rounded-full bg-sage-light flex items-center justify-center mt-0.5">
                    <svg className="w-4 h-4 text-sage-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-slate text-base mb-1">{item?.title}</h3>
                    <p className="font-body text-sm text-slate-fog leading-relaxed">{item?.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <a
                href="/loans/new"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-sky text-white rounded-full text-sm font-semibold font-body hover:bg-sky-dark transition-all duration-200 shadow-sky/30 shadow-md"
              >
                Check My Rate
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </a>
              <p className="text-xs text-slate-fog font-body mt-3">No hard credit pull to check your rate</p>
            </div>
          </div>

          {/* RIGHT: Rates overview card */}
          <div className="reveal-right">
            <div className="bg-white rounded-4xl border border-slate-mist/40 shadow-card overflow-hidden">
              {/* Card header */}
              <div className="px-8 py-6 bg-sky text-white">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white">Rates Overview</h3>
                </div>
                <p className="text-xs text-white/70 font-body">Estimated ranges — your rate depends on credit profile</p>
              </div>

              {/* Rates list */}
              <div className="divide-y divide-slate-mist/30">
                {RATES?.map((rate, idx) => (
                  <div
                    key={rate?.label}
                    className={`flex items-center justify-between px-8 py-5 ${idx % 2 === 0 ? 'bg-white' : 'bg-cloud/50'}`}
                  >
                    <span className="font-body text-sm text-slate-fog">{rate?.label}</span>
                    <span className="font-heading font-bold text-slate text-sm">{rate?.value}</span>
                  </div>
                ))}
              </div>

              {/* Card footer */}
              <div className="px-8 py-5 bg-cloud/60 border-t border-slate-mist/30">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-5 h-5 rounded-full bg-sky-light flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-sky" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-xs text-slate-fog font-body leading-relaxed">
                    Rates and terms are subject to lender approval. APR includes origination fees where applicable. Checking your rate does not affect your credit score.
                  </p>
                </div>
              </div>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-3 mt-5">
              {[
                { icon: '🔒', label: 'Bank-Level Encryption', sub: '256-bit SSL secured' },
                { icon: '⚡', label: 'Fast Decisions', sub: 'Often within minutes' },
              ]?.map((badge) => (
                <div key={badge?.label} className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-mist/40">
                  <span className="text-xl">{badge?.icon}</span>
                  <div>
                    <p className="text-xs font-semibold text-slate font-body leading-tight">{badge?.label}</p>
                    <p className="text-xs text-slate-fog font-body">{badge?.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
